import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Selection state for the asset grid.
 *
 * Selection is the grid's primary interaction, not a side feature: bulk edit
 * and publish both operate on whatever is selected, so this hook owns the
 * range/toggle semantics rather than leaving each card to reinvent them.
 *
 * The three gestures follow the file-manager convention users already have in
 * their fingers:
 *
 *   click            replace the selection with this one asset
 *   cmd/ctrl-click   add or remove this one asset, keep the rest
 *   shift-click      select the inclusive range from the anchor to here
 *
 * The anchor is an asset id, not an index. Indices are positions in a list
 * that refetches underneath us — an anchor of `4` silently means a different
 * asset after a publish reorders the page, so a shift-click would select a
 * range the user never saw. An id either still exists (and its position is
 * looked up fresh) or it doesn't (and the range degrades to a plain click).
 */

export interface AssetSelection {
  selectedIds: Set<string>;
  /** Kept separate from `selectedIds.size` so callers don't reach into the Set. */
  count: number;
  isSelected: (id: string) => boolean;
  /**
   * Whether a click on this asset can select it. The cards read this rather
   * than working it out again, so a card never looks clickable for an asset
   * that `select` would ignore.
   */
  isSelectable: (id: string) => boolean;
  /** Handles all three gestures; pass the modifier keys from the click event. */
  select: (id: string, modifiers?: { shift?: boolean; meta?: boolean }) => void;
  selectAll: () => void;
  clear: () => void;
  /**
   * Set the selection outright, bypassing the click gestures.
   *
   * For narrowing to a result the user did not pick by hand — a partial publish
   * leaves the skipped rows selected so the bulk editor stays on the ones that
   * still need fixing.
   */
  replace: (ids: string[]) => void;
}

/**
 * @param orderedIds The ids currently rendered, in display order. Shift-ranges
 *   are resolved against this, so it must match what the user sees — including
 *   after filtering, not the unfiltered set.
 * @param disabledIds Optional set of asset IDs that cannot be selected (e.g. expired assets).
 */
export function useAssetSelection(orderedIds: string[], disabledIds?: Set<string>): AssetSelection {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const anchorId = useRef<string | null>(null);

  // Index lookup for shift-ranges. Built once per list change rather than
  // per click, which would make a range selection O(n) on every keystroke-fast
  // repeat of shift-click.
  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    orderedIds.forEach((id, index) => map.set(id, index));
    return map;
  }, [orderedIds]);

  // Drop ids that are no longer in the list or have become disabled (e.g. expired).
  // Without this, publishing moves an asset out of the Staged tab or status changes
  // leave it selected and invisible/unactionable.
  useEffect(() => {
    setSelectedIds((current) => {
      if (current.size === 0) return current;
      const survivors = new Set<string>();
      for (const id of current) {
        if (indexById.has(id) && (!disabledIds || !disabledIds.has(id))) {
          survivors.add(id);
        }
      }
      // Same-size means nothing was pruned; returning `current` keeps the
      // reference stable so consumers don't re-render on every list refetch.
      return survivors.size === current.size ? current : survivors;
    });

    if (
      anchorId.current !== null &&
      (!indexById.has(anchorId.current) || (disabledIds && disabledIds.has(anchorId.current)))
    ) {
      anchorId.current = null;
    }
  }, [indexById, disabledIds]);

  const select = useCallback(
    (id: string, modifiers: { shift?: boolean; meta?: boolean } = {}) => {
      if (disabledIds && disabledIds.has(id)) return;

      const { shift = false, meta = false } = modifiers;
      const anchor = anchorId.current;

      // Shift with a live anchor is the only gesture that reads more than the
      // clicked id. A shift-click with no usable anchor (first click, or the
      // anchor was pruned) falls through to plain-click rather than doing
      // nothing, which is what every file manager does.
      if (shift && anchor !== null && indexById.has(anchor) && indexById.has(id)) {
        const from = indexById.get(anchor)!;
        const to = indexById.get(id)!;
        const [start, end] = from <= to ? [from, to] : [to, from];
        const range = orderedIds.slice(start, end + 1);
        const valid = disabledIds ? range.filter((item) => !disabledIds.has(item)) : range;
        // Ranges replace rather than extend the selection, so dragging the
        // shift-click back and forth previews different ranges instead of
        // accumulating every range the pointer passed through.
        setSelectedIds(new Set(valid));
        // Anchor deliberately not moved: successive shift-clicks re-range from
        // the same origin, so a user who overshoots can correct by clicking
        // again rather than restarting.
        return;
      }

      if (meta) {
        setSelectedIds((current) => {
          const next = new Set(current);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        });
        anchorId.current = id;
        return;
      }

      // Plain click on the only selected asset deselects it, so the drawer can
      // be dismissed with the same gesture that opened it.
      setSelectedIds((current) =>
        current.size === 1 && current.has(id) ? new Set() : new Set([id]),
      );
      anchorId.current = id;
    },
    [indexById, orderedIds, disabledIds],
  );

  const selectAll = useCallback(() => {
    const valid = disabledIds ? orderedIds.filter((id) => !disabledIds.has(id)) : orderedIds;
    setSelectedIds(new Set(valid));
    anchorId.current = valid[0] ?? null;
  }, [orderedIds, disabledIds]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    anchorId.current = null;
  }, []);

  const replace = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
    anchorId.current = ids[0] ?? null;
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);
  const isSelectable = useCallback((id: string) => !disabledIds?.has(id), [disabledIds]);

  return {
    selectedIds,
    count: selectedIds.size,
    isSelected,
    isSelectable,
    select,
    selectAll,
    clear,
    replace,
  };
}
